import { validateQuad } from "./qr-pose.js";
import { t as createDetectionScheduler } from "../../detection-scheduler-PSFJiKJq.js";
import { buildQrGpsVotes } from "./qr-gps-vote.js";
//#region ../src/ar/qr/qr-tracking-controller.ts
function createQrTrackingController(config) {
	const { frontEnd, solvePose, fetchLevel, dispatchVotes, onDetection, onRawDetection, shouldCacheLevel, resolveSizeM, resolveStablePose, getIntrinsics, syntheticAccuracyM, voteBaselineM, voteCount, isPlausible, onStatus, onLocked, onError, minIntervalMs = 150, requiredLockCount = 3, now } = config;
	const timestampNow = now ?? (() => Date.now());
	let status = "idle";
	const levelCache = /* @__PURE__ */ new Map();
	let active = null;
	let disposed = false;
	function setStatus(next) {
		if (status === next) return;
		status = next;
		onStatus?.(next);
	}
	async function ensureLevel(url) {
		const cached = levelCache.get(url);
		if (cached) return cached;
		setStatus("loading-level");
		const level = await fetchLevel(url);
		if (shouldCacheLevel === void 0 || shouldCacheLevel(level)) levelCache.set(url, level);
		return level;
	}
	async function detect(frame) {
		const { image, cameraPose } = frame;
		if (status === "idle" || status === "error") setStatus("scanning");
		const detection = await frontEnd.detect(image);
		if (disposed) return null;
		if (!detection) {
			active = null;
			return null;
		}
		if (onRawDetection && validateQuad(detection.corners).ok) onRawDetection({
			text: detection.text,
			corners: detection.corners,
			cameraPose,
			imageWidth: image.width,
			imageHeight: image.height,
			timestamp: frame.capturedAtMs,
			...detection.orderSource ? { orderSource: detection.orderSource } : {}
		});
		const level = await ensureLevel(detection.text);
		if (disposed) return null;
		const sizeM = level.qr.physicalSizeM ?? resolveSizeM?.(detection.text, level) ?? null;
		if (sizeM === null || !(sizeM > 0) || !Number.isFinite(sizeM)) {
			active = null;
			if (status === "loading-level") setStatus("scanning");
			return null;
		}
		const intrinsics = getIntrinsics(image);
		if (!intrinsics) {
			active = null;
			return null;
		}
		const solution = solvePose({
			imagePoints: detection.corners,
			sizeM,
			intrinsics,
			cameraPose
		});
		if (!solution) {
			active = null;
			return null;
		}
		if (isPlausible && !isPlausible(solution, cameraPose)) {
			active = null;
			return null;
		}
		active = {
			level,
			text: detection.text,
			sizeM,
			corners: detection.corners,
			cameraPose,
			imageWidth: image.width,
			imageHeight: image.height,
			intrinsics,
			orderSource: detection.orderSource
		};
		return solution;
	}
	const scheduler = createDetectionScheduler({
		detect,
		minIntervalMs,
		requiredLockCount,
		now,
		onLocked: (solution) => {
			const current = active;
			if (!current) return;
			const { level, text, sizeM } = current;
			onDetection?.({
				text,
				qrPoseWorld: solution.qrPoseWorld,
				qrPoseInCamera: solution.qrPoseInCamera,
				reprojectionErrorPx: solution.reprojectionErrorPx,
				timestamp: timestampNow(),
				corners: current.corners,
				cameraPose: current.cameraPose,
				imageWidth: current.imageWidth,
				imageHeight: current.imageHeight,
				intrinsics: current.intrinsics,
				...current.orderSource ? { orderSource: current.orderSource } : {}
			});
			if (level.qr.geo) {
				const votePose = resolveStablePose ? resolveStablePose(text) : solution.qrPoseWorld;
				if (votePose) dispatchVotes(buildQrGpsVotes({
					qrPoseWorld: votePose,
					sizeM,
					qrGeo: level.qr.geo,
					syntheticAccuracyM,
					...voteBaselineM !== void 0 ? { baselineM: voteBaselineM } : {},
					...voteCount !== void 0 ? { count: voteCount } : {}
				}));
			}
			setStatus("tracking");
			onLocked?.(solution, level);
		},
		onMiss: () => {
			if (status === "tracking") setStatus("scanning");
		},
		onError: (err) => {
			active = null;
			setStatus("error");
			onError?.(err);
		}
	});
	return {
		offerFrame(frame) {
			scheduler.offerFrame(frame);
		},
		isBusy() {
			return scheduler.inFlight;
		},
		get status() {
			return status;
		},
		reset() {
			levelCache.clear();
			active = null;
			setStatus("idle");
		},
		dispose() {
			disposed = true;
			scheduler.dispose();
			levelCache.clear();
			active = null;
		}
	};
}
//#endregion
export { createQrTrackingController };
